import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Camera, ListChecks, Sparkles, Wallet, Wrench, type LucideIcon } from 'lucide-react';
import { useVertical } from '../../context/VerticalContext';
import { FlowDiagram } from '../help/FlowDiagram';

interface Slide {
    icon: LucideIcon;
    title: string;
    body: string[];
    /** Rendered between the first and remaining paragraphs. */
    visual?: 'flow';
}

/**
 * First-run introduction. Explains what the app is for before it explains how
 * to use it — someone who doesn't know what Safe-to-Spend is cannot be taught
 * where to click.
 *
 * Skippable at every step, and skipping is a first-class button rather than a
 * greyed-out afterthought: people who want to explore on their own should not
 * have to defeat the tour to do it.
 */
export const WelcomeCarousel = ({
    userName,
    onComplete,
    onSkip,
}: {
    userName?: string;
    onComplete: () => void;
    onSkip: () => void;
}) => {
    const { molded } = useVertical();
    const { jargon } = molded;
    const [index, setIndex] = useState(0);
    const [direction, setDirection] = useState(1);

    const slides: Slide[] = [
        {
            icon: Sparkles,
            title: userName ? `Welcome, ${userName}.` : 'Welcome to ForgeDash.',
            body: [
                `This is the business side of your ${jargon.trade_label.toLowerCase()} in one place — work, invoices, costs, and tax.`,
                'Two minutes here and the rest will make sense. You can skip and come back any time.',
            ],
        },
        {
            icon: Wallet,
            title: 'The work comes first',
            body: [
                `You run the ${jargon.trade_label.toLowerCase()} out of your ${jargon.action_label.toLowerCase()} list. Everything financial follows from it.`,
                '**Safe-to-Spend** is a byproduct of the work you have done and been paid for — the money you can actually spend, after setting aside tax, National Insurance and VAT.',
                'You do not manage that number. You manage the jobs, and the number tells you where they have left you.',
            ],
        },
        {
            icon: Wrench,
            title: 'Work flows one way',
            visual: 'flow',
            body: [
                'Every piece of work follows the same path:',
                'Two appointments — one to find out what is wrong, one to put it right, with an agreed price in between.',
                'You do the work as normal; the app keeps the books as you go. There is no separate bookkeeping step.',
            ],
        },
        {
            icon: Camera,
            title: 'Paid in, receipts out',
            body: [
                'When an invoice is marked **paid**, your tax, NI and VAT reserves update.',
                'When you upload a supplier or expense invoice, your costs land and every figure realigns to UK rules — tax, NI, VAT and profit.',
                'A receipt you never log is tax relief you paid for and threw away. Snap it at the counter.',
            ],
        },
        {
            icon: ListChecks,
            title: 'Where to start',
            body: [
                `The four buttons at the top of your dashboard — **New Appointment**, **New Diagnostic**, **New Quote**, **New ${jargon.action_label}** — let you join the loop wherever the work actually starts.`,
                'Below them is a short setup list. Work through it when you have a minute and your numbers will start telling you the truth.',
                'Everything else is in **Help & Guides** — at the bottom of the sidebar on a computer, or under the ☰ menu on your phone.',
            ],
        },
    ];

    const slide = slides[index];
    const isLast = index === slides.length - 1;
    const Icon = slide.icon;

    const go = (next: number) => {
        setDirection(next > index ? 1 : -1);
        setIndex(next);
    };

    // Minimal inline emphasis so slide copy can bold a term without pulling the
    // whole markdown renderer into the first-run path.
    const renderBody = (text: string) =>
        text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
            part.startsWith('**') && part.endsWith('**')
                ? <strong key={i} className="font-semibold text-fg">{part.slice(2, -2)}</strong>
                : <span key={i}>{part}</span>
        );

    return (
        <div
            className="fd-fullscreen fixed inset-0 z-[80] flex items-center justify-center bg-scrim/85 backdrop-blur-sm
                       px-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]"
            role="dialog"
            aria-modal="true"
            aria-label="Welcome to ForgeDash"
        >
            <div className="w-full max-w-md rounded-2xl bg-panel border border-line shadow-2xl overflow-hidden flex flex-col max-h-full">
                <div className="h-1 w-full bg-gold-fill shrink-0" />

                <div className="flex justify-end px-3 pt-3 shrink-0">
                    <button
                        onClick={onSkip}
                        className="text-[13px] px-3 py-1.5 rounded-lg text-fg-3 hover:text-fg hover:bg-card transition-colors"
                    >
                        Skip tour
                    </button>
                </div>

                <div className="px-7 pb-2 overflow-y-auto">
                    <AnimatePresence mode="wait" initial={false}>
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, x: direction * 24 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: direction * -24 }}
                            transition={{ duration: 0.22, ease: 'easeOut' }}
                        >
                            <div className="w-14 h-14 rounded-2xl grid place-items-center bg-gold-fill/10 border border-gold/25 mb-5">
                                <Icon className="w-7 h-7 text-gold" strokeWidth={2} />
                            </div>

                            <h2 className="text-2xl font-bold font-display text-fg tracking-tight mb-3">
                                {slide.title}
                            </h2>

                            {slide.body.map((paragraph, i) => (
                                <div key={i}>
                                    <p className="text-[15px] leading-relaxed text-fg-2 mb-3">
                                        {renderBody(paragraph)}
                                    </p>
                                    {i === 0 && slide.visual === 'flow' && <FlowDiagram variant="chips" />}
                                </div>
                            ))}
                        </motion.div>
                    </AnimatePresence>
                </div>

                <div className="flex items-center justify-between gap-4 px-7 py-5 shrink-0">
                    <div className="flex items-center gap-1.5" aria-hidden="true">
                        {slides.map((_, i) => (
                            <span
                                key={i}
                                className={`h-1.5 rounded-full transition-all duration-200 ${i === index ? 'w-5 bg-gold-fill' : 'w-1.5 bg-pressed'}`}
                            />
                        ))}
                    </div>

                    <div className="flex items-center gap-2">
                        {index > 0 && (
                            <button
                                onClick={() => go(index - 1)}
                                aria-label="Previous"
                                className="flex items-center justify-center w-11 h-11 rounded-lg text-fg-3 hover:text-fg hover:bg-card transition-colors"
                            >
                                <ArrowLeft className="w-4 h-4" />
                            </button>
                        )}
                        <button
                            onClick={() => (isLast ? onComplete() : go(index + 1))}
                            className="flex items-center gap-2 h-11 px-5 rounded-lg font-bold fd-btn-primary transition-all active:scale-[0.98]"
                        >
                            {isLast ? 'Get started' : 'Next'}
                            {!isLast && <ArrowRight className="w-4 h-4" />}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
